import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import DefaultButton from "./buttons/DefaultButton";
import UserAvatar from "./UserAvatar";
import PlaylistCover from "./PlaylistCover";
import Playlist from "../../models/Playlist";
import { fetchPlaylistsByUser, notifyPlaylistsChanged, setPlaylistFollow } from "../../services/playlist";
import { UserSearchResult } from "../../services/user";
import { colors } from "../../theme";

interface UserPlaylistsDialogProps {
    /** The person whose playlists to show. Null closes the dialog. */
    user: UserSearchResult | null;
    onClose: () => void;
}

/**
 * Someone else's profile: everything they've made, with a follow control per row.
 * Following adds the playlist to your own library sidebar, which is why each change
 * announces itself rather than only updating this list.
 */
export default function UserPlaylistsDialog(props: UserPlaylistsDialogProps) {
    const { user, onClose } = props;
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Ids mid-request, so a row can't be clicked twice into a contradictory state.
    const [pending, setPending] = useState<number[]>([]);

    const userId = user?.id;

    const load = useCallback(async () => {
        if (userId === undefined) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setPlaylists(await fetchPlaylistsByUser(userId));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load their playlists");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleToggleFollow = async (playlist: Playlist) => {
        const next = !playlist.following;
        setPending(prev => [...prev, playlist.id]);
        try {
            // Trust the server's answer over the optimistic guess - it is the one that
            // knows whether the row was already followed in another tab.
            const following = await setPlaylistFollow(playlist.id, next);
            setPlaylists(prev => prev.map(p => (p.id === playlist.id ? { ...p, following } : p)));
            notifyPlaylistsChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't update follow");
        } finally {
            setPending(prev => prev.filter(id => id !== playlist.id));
        }
    };

    return (
        <Dialog
            open={user !== null}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                        borderRadius: 3,
                    },
                },
            }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <UserAvatar username={user?.username} imageUrl={user?.profilePictureUrl} size={40} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }} noWrap>
                        {user?.username}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                        {playlists.length} playlist{playlists.length === 1 ? "" : "s"}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && (
                    <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                        {error}
                    </Typography>
                )}

                {!loading && playlists.length === 0 && !error && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: 14, py: 2 }}>
                        {user?.username} hasn't made any playlists yet.
                    </Typography>
                )}

                {playlists.map(playlist => (
                    <Box
                        key={playlist.id}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 1.25,
                            borderBottom: `1px solid ${colors.line}`,
                        }}
                    >
                        <PlaylistCover images={playlist.imageUrl ? [playlist.imageUrl] : []} size={44} borderRadius={1} />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 15 }} noWrap>
                                {playlist.playlistName}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: colors.textMuted }} noWrap>
                                {playlist.following ? "In your library" : "Not following"}
                            </Typography>
                        </Box>
                        <DefaultButton
                            text={playlist.following ? "Following" : "Follow"}
                            variant={playlist.following ? "secondary" : "primary"}
                            size="small"
                            disabled={pending.includes(playlist.id)}
                            onClick={() => handleToggleFollow(playlist)}
                        />
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
}
